import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, isRef as _isRef, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vModelCheckbox as _vModelCheckbox, createElementVNode as _createElementVNode, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "my-planning" }
const _hoisted_2 = { class: "planning-options" }
const _hoisted_3 = { class: "checkbox-label" }
const _hoisted_4 = { class: "checkbox-label" }
const _hoisted_5 = { class: "checkbox-label" }
const _hoisted_6 = { class: "checkbox-label" }

import { computed, ref, onMounted, onUnmounted, watch } from "vue";
import { useLocalStorage } from "@vueuse/core";
import { useQuery, useMutation, useApolloClient } from "@vue/apollo-composable";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import PlanningToolbar from "./PlanningToolbar.vue";
import PlanningFilterPanel from "./PlanningFilters.vue";
import PlanningCalendar from "./PlanningCalendar.vue";
import PlanningModal from "./PlanningModal.vue";
import PlanningKpiCard from "./PlanningKpiCard.vue";
import EditTimesheet from "@/components/CRUXEntity/EditTimesheet.vue";
import { GET_EMPLOYEES, GET_USER } from "@/graphql/user/queries";
import { GET_DEFAULT_ROLES } from "@/graphql/defaultRole/queries";
import { isAdmin } from "@/helpers/router/queries";
import {
  GET_PLANNING,
  GET_PLANNING_KPI_FOR_PERIOD,
  GET_PLANNED_TIME_FOR_EDIT,
  GET_TIME_OFF_FOR_EDIT,
} from "@/graphql/planning/queries";
import {
  CREATE_PLANNED_TIME,
  UPDATE_PLANNED_TIME,
  DELETE_PLANNED_TIME,
  CREATE_TIME_OFF,
  UPDATE_TIME_OFF,
  DELETE_TIME_OFF,
  UPDATE_PLANNED_TIME_OCCURRENCE,
  DELETE_PLANNED_TIME_OCCURRENCE,
  UPDATE_TIME_OFF_OCCURRENCE,
  DELETE_TIME_OFF_OCCURRENCE,
} from "@/graphql/planning/mutations";
import type { LeaveType } from "@/helpers/project/typesDefinition";
import {
  getPlanningPeriodLabel,
  getPlanningPeriodStartDate,
  getVisibleDays,
} from "@/helpers/planning/planningDates";
import { normalizePlanningUser } from "@/helpers/planning/planningNormalizers";
import { usePlanningFilters } from "@/composables/planning/usePlanningFilters";
import type {
  PlanningEditMode,
  PlanningFormData,
  PlanningItemType,
  PlanningProject,
  PlanningUser,
  VisibleDay,
} from "./type";
import type { TimesheetFormValue } from "@/helpers/client/typesDefinition";
import {
  DEFAULT_DURATION_PER_DAY,
  DEFAULT_LEAVE_TYPE,
  normalizeProjects,
  normalizePlannedTimes,
  normalizeTimeOffs,
  normalizeHolidaysByUser,
  normalizeTimesheetEntries,
  type PlanningPlannedTime,
  type PlanningTimeOff,
  type PlanningHoliday,
  type PlanningTimesheetEntry,
  type TimesheetSuggestion,
} from "@/helpers/planning/planningDataNormalizers";
import dayjs from "dayjs";
import enUK from "@/locales/en-UK.json";

type MessageSchema = typeof enUK;

interface UsersResult {
  users: PlanningUser[];
}

interface DefaultRolesResult {
  defaultRoles: {
    status: string;
    error: string | null;
    data: Array<{
      id: string;
      name: string;
      hourlyRate: number;
    }>;
  };
}

const SHOW_ONLY_ME_KEY = "planning-showOnlyMe";

export default /*@__PURE__*/_defineComponent({
  __name: 'Planning',
  setup(__props) {

const toast = useToast();
const { t } = useI18n<[MessageSchema], "en-UK">({
  useScope: "global",
});

// Helper to show persistent planning notification
const showPlanningNotification = (
  type: "success" | "warning" | "error",
  message: string,
  details?: string,
) => {
  const toastOptions = {
    timeout: 5000, // Persistent - stays until manually closed
    closeOnClick: true,
    draggable: true,
  };

  if (details) {
    toast[type](`${message}\n${details}`, toastOptions);
  } else {
    toast[type](message, toastOptions);
  }
};

const { result: usersResult, refetch: refetchUsers } =
  useQuery<UsersResult>(GET_EMPLOYEES);

// Query to fetch default roles from the default_roles table
const { result: defaultRolesResult } =
  useQuery<DefaultRolesResult>(GET_DEFAULT_ROLES);

const defaultRoles = computed(() => {
  return defaultRolesResult.value?.defaultRoles?.data ?? [];
});

const hasStoredShowOnlyMe = localStorage.getItem(SHOW_ONLY_ME_KEY) !== null;
const showOnlyMe = useLocalStorage<boolean>(SHOW_ONLY_ME_KEY, true);

const { result: currentUserResult, onResult: onCurrentUserResult } =
  useQuery(GET_USER);
const currentUser = computed(() => currentUserResult.value?.user);
const isUserAdmin = computed(() => isAdmin(currentUser.value?.accessRights));

// Set showOnlyMe based on admin status when query resolves (only if no stored preference)
onCurrentUserResult((r) => {
  const userIsAdmin = isAdmin(r.data.user?.accessRights);
  if (!hasStoredShowOnlyMe) {
    showOnlyMe.value = !userIsAdmin;
  }
});

const users = computed<PlanningUser[]>(() => {
  return (usersResult.value?.users ?? [])
    .slice()
    .sort((a, b) => {
      const nameA = (a.firstName ?? "").toLowerCase();
      const nameB = (b.firstName ?? "").toLowerCase();
      return nameA.localeCompare(nameB);
    })
    .map((user) => {
      return normalizePlanningUser(user);
    });
});

const currentPeriodOffset = ref(0);
const viewMode = useLocalStorage<"two_weeks" | "month">(
  "planning-viewMode",
  "two_weeks",
);
const showModal = ref(false);
const isEditing = ref(false);
const selectedItemId = ref<string | null>(null);
const selectedItemType = ref<PlanningItemType | null>(null);
const planningEditMode = ref<PlanningEditMode>("all");
const editingParentId = ref<string | null>(null);

// Store fetched parent and occurrence data for switching between edit modes
const fetchedPlannedTimeParentData = ref<PlanningPlannedTime | null>(null);
const fetchedPlannedTimeOccurrenceData = ref<PlanningPlannedTime | null>(null);
const fetchedTimeOffParentData = ref<PlanningTimeOff | null>(null);
const fetchedTimeOffOccurrenceData = ref<PlanningTimeOff | null>(null);

const formData = ref<PlanningFormData>({
  userId: null,
  projectId: null,
  projectRoleId: null,
  startDate: "",
  endDate: "",
  frequency: null,
  repeatEndDate: null,
  durationPerDay: DEFAULT_DURATION_PER_DAY,
  notes: "",
  tentative: false,
  type: DEFAULT_LEAVE_TYPE,
});

// Computed property to get the occurrence's tentative value for "single" edit mode
const occurrenceTentative = computed(() => {
  return fetchedPlannedTimeOccurrenceData.value?.tentative ?? false;
});

const showLoggedTime = useLocalStorage("planning-showLoggedTime", true);
const showPlannedTime = useLocalStorage("planning-showPlannedTime", true);
const showLoggedTimeSuggestions = useLocalStorage(
  "planning-showLoggedTimeSuggestions",
  true,
);

const showTimesheetModal = ref(false);
const selectedTimesheet = ref<TimesheetFormValue | null>(null);

// Custom period filter state
const customPeriodStart = ref<string | null>(null);
const customPeriodEnd = ref<string | null>(null);

const handlePeriodUpdate = (start: string | null, end: string | null) => {
  customPeriodStart.value = start;
  customPeriodEnd.value = end;
};

const periodStartDate = computed(() => {
  if (viewMode.value === "month") {
    return dayjs()
      .add(currentPeriodOffset.value, "month")
      .startOf("month")
      .format("YYYY-MM-DD");
  }

  return getPlanningPeriodStartDate(currentPeriodOffset.value);
});

const periodEndDate = computed(() => {
  if (viewMode.value === "month") {
    return dayjs(periodStartDate.value).endOf("month").format("YYYY-MM-DD");
  }

  let workDays = 0;
  let currentDate = dayjs(periodStartDate.value);

  while (workDays < 10) {
    const dayOfWeek = currentDate.day();

    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      workDays++;

      if (workDays === 10) break;
    }

    currentDate = currentDate.add(1, "day");
  }

  return currentDate.format("YYYY-MM-DD");
});

const planningVariables = computed(() => {
  return {
    startDate: periodStartDate.value,
    endDate: periodEndDate.value,
  };
});

const { result: planningResult, refetch: refetchPlanning } = useQuery(
  GET_PLANNING,
  planningVariables,
  {
    fetchPolicy: "network-only",
  },
);

const { mutate: createPlannedTimeMutate, onDone: onPlannedTimeCreated } =
  useMutation(CREATE_PLANNED_TIME);
onPlannedTimeCreated(() => {
  refetchUsers();
  refetchPlanning();
});

const { mutate: updatePlannedTimeMutate, onDone: onPlannedTimeUpdated } =
  useMutation(UPDATE_PLANNED_TIME);
onPlannedTimeUpdated(() => {
  refetchUsers();
  refetchPlanning();
});

const { mutate: deletePlannedTimeMutate, onDone: onPlannedTimeDeleted } =
  useMutation(DELETE_PLANNED_TIME);
onPlannedTimeDeleted(() => {
  refetchUsers();
  refetchPlanning();
});

const { mutate: createTimeOffMutate, onDone: onTimeOffCreated } =
  useMutation(CREATE_TIME_OFF);
onTimeOffCreated(() => {
  refetchUsers();
  refetchPlanning();
});

const { mutate: updateTimeOffMutate, onDone: onTimeOffUpdated } =
  useMutation(UPDATE_TIME_OFF);
onTimeOffUpdated(() => {
  refetchUsers();
  refetchPlanning();
});

const { mutate: deleteTimeOffMutate, onDone: onTimeOffDeleted } =
  useMutation(DELETE_TIME_OFF);
onTimeOffDeleted(() => {
  refetchUsers();
  refetchPlanning();
});

// Single occurrence mutations
const {
  mutate: updatePlannedTimeOccurrenceMutate,
  onDone: onPlannedTimeOccurrenceUpdated,
} = useMutation(UPDATE_PLANNED_TIME_OCCURRENCE);
onPlannedTimeOccurrenceUpdated(() => {
  refetchUsers();
  refetchPlanning();
});
const {
  mutate: deletePlannedTimeOccurrenceMutate,
  onDone: onPlannedTimeOccurrenceDeleted,
} = useMutation(DELETE_PLANNED_TIME_OCCURRENCE);
onPlannedTimeOccurrenceDeleted(() => {
  refetchUsers();
  refetchPlanning();
});
const {
  mutate: updateTimeOffOccurrenceMutate,
  onDone: onTimeOffOccurrenceUpdated,
} = useMutation(UPDATE_TIME_OFF_OCCURRENCE);
onTimeOffOccurrenceUpdated(() => {
  refetchUsers();
  refetchPlanning();
});
const {
  mutate: deleteTimeOffOccurrenceMutate,
  onDone: onTimeOffOccurrenceDeleted,
} = useMutation(DELETE_TIME_OFF_OCCURRENCE);
onTimeOffOccurrenceDeleted(() => {
  refetchUsers();
  refetchPlanning();
});

const projects = computed<PlanningProject[]>(() => {
  return normalizeProjects(planningResult.value?.planning?.projects ?? []);
});

// The project currently being edited. Prefers the fresh data from the
// plannedTimeForEdit query (which includes archived projects), falls back to the
// active projects list. Used so the readonly project dropdown in the edit modal
// can still display an archived project.
const editingProject = computed<PlanningProject | null>(() => {
  if (!isEditing.value || selectedItemType.value !== "planned_time") {
    return null;
  }

  const projectId = formData.value.projectId;
  if (!projectId) {
    return null;
  }

  const occurrenceProject = fetchedPlannedTimeOccurrenceData.value?.project;
  if (occurrenceProject && occurrenceProject.id === projectId) {
    return occurrenceProject;
  }

  return projects.value.find((project) => project.id === projectId) ?? null;
});

const plannedTimes = computed<PlanningPlannedTime[]>(() => {
  return normalizePlannedTimes(
    planningResult.value?.planning?.plannedTimes ?? [],
  );
});

const timeOffs = computed<PlanningTimeOff[]>(() => {
  return normalizeTimeOffs(planningResult.value?.planning?.timeOffs ?? []);
});

const userHolidaysMap = computed(() => {
  return planningResult.value?.planning?.userHolidaysMap ?? [];
});

const holidaysByUser = computed<Record<string, PlanningHoliday[]>>(() => {
  return normalizeHolidaysByUser(userHolidaysMap.value);
});

const timesheetEntries = computed<PlanningTimesheetEntry[]>(() => {
  return normalizeTimesheetEntries(
    planningResult.value?.planning?.timesheetEntries ?? [],
  );
});

const visibleDays = computed<VisibleDay[]>(() => {
  if (viewMode.value === "month") {
    const days: VisibleDay[] = [];
    let currentDate = dayjs(periodStartDate.value);
    const endDate = dayjs(periodEndDate.value);

    while (
      currentDate.isBefore(endDate) ||
      currentDate.isSame(endDate, "day")
    ) {
      const dayOfWeek = currentDate.day();

      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        days.push({
          date: currentDate.format("YYYY-MM-DD"),
          dayName: currentDate.format("ddd"),
          dayDate: currentDate.date(),
          dayOfWeek,
          monthLabel: currentDate.format("MMM"),
        });
      }

      currentDate = currentDate.add(1, "day");
    }

    return days;
  }

  return getVisibleDays(periodStartDate.value);
});

const weekRangeLabel = computed(() => {
  if (viewMode.value === "month") {
    return dayjs(periodStartDate.value).format("MMM YYYY");
  }

  return getPlanningPeriodLabel(visibleDays.value);
});

const {
  showFilterPanel,
  filterSearch,
  activeFilterTab,
  filterTabs,
  filteredFilterOptions,
  filteredUsers,
  filteredPlannedTimes,
  filteredTimeOffs,
  hasActiveFilters,
  activeFilterCount,
  toggleFilterPanel,
  isFilterSelected,
  toggleFilter,
  clearAllFilters,
} = usePlanningFilters({
  users,
  projects: computed(() => projects.value),
  plannedTimes: computed(() => plannedTimes.value),
  timeOffs: computed(() => timeOffs.value),
  defaultRoles: defaultRoles,
  timesheetEntries: computed(() => timesheetEntries.value),
  showLoggedTime: showLoggedTime,
  showPlannedTime: showPlannedTime,
});

const displayedUsers = computed(() => {
  if (showOnlyMe.value && currentUser.value) {
    return filteredUsers.value.filter(
      (user) => user.id === currentUser.value?.id,
    );
  }
  return filteredUsers.value;
});

// Smaller min height by default; when few users are shown, allow rows to be taller
const planningMinRowHeight = computed(() => {
  return displayedUsers.value.length <= 2 ? 150 : 60;
});

const filteredHolidays = computed(() => {
  return displayedUsers.value.map((user) => {
    const userHolidays = holidaysByUser.value[user.id] ?? [];

    return {
      user,
      holidays: userHolidays,
    };
  });
});

const filteredTimesheetEntries = computed(() => {
  return timesheetEntries.value.filter((entry) => {
    return displayedUsers.value.some((user) => user.id === entry.user.id);
  });
});

// KPI Card computed properties
const currentWeekNumber = computed(() => {
  // Calculate week number manually (ISO week)
  const date = dayjs(periodStartDate.value);
  const startOfYear = dayjs(date).startOf("year");
  const daysSinceStartOfYear = date.diff(startOfYear, "day");
  return Math.ceil((daysSinceStartOfYear + startOfYear.day() + 1) / 7);
});

const currentMonthLabel = computed(() => {
  return dayjs(periodStartDate.value).format("MMM YYYY");
});

// Week KPI - use backend query for consistency with month/custom period
const weekStartDate = computed(() => {
  return periodStartDate.value;
});

const weekEndDate = computed(() => {
  // Find the last day of the first visible week
  // Stop at the next Monday or end of visible days
  const firstWeekDays: VisibleDay[] = [];
  for (const day of visibleDays.value) {
    if (firstWeekDays.length > 0 && day.dayOfWeek === 1) {
      // Found next Monday, stop
      break;
    }
    firstWeekDays.push(day);
  }
  return firstWeekDays[firstWeekDays.length - 1]?.date ?? periodEndDate.value;
});

// Fetch week KPI from backend for consistency (includes paid_leave + sick_leave)
const weekKpiVariables = computed(() => {
  return {
    startDate: weekStartDate.value,
    endDate: weekEndDate.value,
    userId: currentUser.value?.id,
  };
});

const { result: weekKpiResult, refetch: refetchWeekKpi } = useQuery<{
  planningKpiForPeriod: { loggedMinutes: number; expectedMinutes: number };
}>(GET_PLANNING_KPI_FOR_PERIOD, weekKpiVariables, {
  fetchPolicy: "network-only",
});

const weekTotal = computed(() => {
  return weekKpiResult.value?.planningKpiForPeriod?.loggedMinutes ?? 0;
});

const weekExpected = computed(() => {
  return weekKpiResult.value?.planningKpiForPeriod?.expectedMinutes ?? 0;
});

// Month KPI
const monthStartDate = computed(() => {
  return dayjs(periodStartDate.value).startOf("month").format("YYYY-MM-DD");
});

const monthEndDate = computed(() => {
  return dayjs(periodStartDate.value).endOf("month").format("YYYY-MM-DD");
});

// Fetch month KPI from backend
const monthKpiVariables = computed(() => {
  return {
    startDate: monthStartDate.value,
    endDate: monthEndDate.value,
    userId: currentUser.value?.id,
  };
});

const { result: monthKpiResult, refetch: refetchMonthKpi } = useQuery<{
  planningKpiForPeriod: { loggedMinutes: number; expectedMinutes: number };
}>(GET_PLANNING_KPI_FOR_PERIOD, monthKpiVariables, {
  fetchPolicy: "network-only",
});

const monthTotal = computed(() => {
  return monthKpiResult.value?.planningKpiForPeriod?.loggedMinutes ?? 0;
});

const monthExpected = computed(() => {
  return monthKpiResult.value?.planningKpiForPeriod?.expectedMinutes ?? 0;
});

// Custom period KPI
const customPeriodKpiEnabled = computed(() =>
  Boolean(customPeriodStart.value && customPeriodEnd.value),
);

const customPeriodKpiVariables = computed(() => ({
  startDate: customPeriodStart.value ?? "",
  endDate: customPeriodEnd.value ?? "",
  userId: currentUser.value?.id,
}));

const { result: customPeriodKpiResult, refetch: refetchCustomPeriodKpi } =
  useQuery<{
    planningKpiForPeriod: { loggedMinutes: number; expectedMinutes: number };
  }>(GET_PLANNING_KPI_FOR_PERIOD, customPeriodKpiVariables, () => ({
    fetchPolicy: "network-only",
    enabled: customPeriodKpiEnabled.value,
  }));

const customPeriodTotal = computed(() => {
  if (!customPeriodStart.value || !customPeriodEnd.value) {
    return 0;
  }
  return customPeriodKpiResult.value?.planningKpiForPeriod?.loggedMinutes ?? 0;
});

const customPeriodExpected = computed(() => {
  if (!customPeriodStart.value || !customPeriodEnd.value) {
    return 0;
  }
  return (
    customPeriodKpiResult.value?.planningKpiForPeriod?.expectedMinutes ?? 0
  );
});

const getUserDisplayName = (user: PlanningUser) => {
  return [user.firstName, user.lastName].filter(Boolean).join(" ");
};

watch(viewMode, () => {
  currentPeriodOffset.value = 0;
});

const goToToday = () => {
  currentPeriodOffset.value = 0;
};

const previousPeriod = () => {
  currentPeriodOffset.value--;
};

const nextPeriod = () => {
  currentPeriodOffset.value++;
};

const handleKeydown = (event: KeyboardEvent) => {
  // Block navigation when a modal is open
  if (showModal.value || showTimesheetModal.value) {
    return;
  }

  if (event.key === "ArrowLeft") {
    previousPeriod();
  } else if (event.key === "ArrowRight") {
    nextPeriod();
  }
};

// Handle trackpad horizontal scroll (2-finger slide)
let horizontalScrollAccumulator = 0;
let horizontalScrollLocked = false;
let horizontalScrollResetTimeout: ReturnType<typeof setTimeout> | null = null;

const handleWheel = (event: WheelEvent) => {
  // Block navigation when a modal is open
  if (showModal.value || showTimesheetModal.value) {
    return;
  }

  // Only handle horizontal scrolling (trackpad 2-finger horizontal slide)
  if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
    return;
  }

  // Prevent default browser horizontal scroll behavior
  event.preventDefault();

  // Keep the gesture locked until trackpad momentum has fully stopped
  if (horizontalScrollResetTimeout) {
    clearTimeout(horizontalScrollResetTimeout);
  }

  horizontalScrollResetTimeout = setTimeout(() => {
    horizontalScrollAccumulator = 0;
    horizontalScrollLocked = false;
    horizontalScrollResetTimeout = null;
  }, 250);

  // Only navigate once per horizontal scroll gesture
  if (horizontalScrollLocked) {
    return;
  }

  horizontalScrollAccumulator += event.deltaX;

  const horizontalScrollThreshold = 80;
  if (Math.abs(horizontalScrollAccumulator) < horizontalScrollThreshold) {
    return;
  }

  horizontalScrollLocked = true;

  if (horizontalScrollAccumulator > 0) {
    // Scrolling right - go to next period
    nextPeriod();
  } else {
    // Scrolling left - go to previous period
    previousPeriod();
  }
};

onMounted(() => {
  window.addEventListener("keydown", handleKeydown);
  window.addEventListener("wheel", handleWheel, { passive: false });
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeydown);
  window.removeEventListener("wheel", handleWheel);

  if (horizontalScrollResetTimeout) {
    clearTimeout(horizontalScrollResetTimeout);
  }
});

const openNewPlannedTime = (user: PlanningUser, day: VisibleDay) => {
  isEditing.value = false;
  selectedItemId.value = null;
  selectedItemType.value = "planned_time";
  planningEditMode.value = "all";
  editingParentId.value = null;

  formData.value = {
    userId: user.id,
    projectId: null,
    projectRoleId: null,
    startDate: day.date,
    endDate: day.date,
    frequency: null,
    repeatEndDate: null,
    durationPerDay: DEFAULT_DURATION_PER_DAY,
    notes: "",
    tentative: false,
    type: DEFAULT_LEAVE_TYPE,
  };

  showModal.value = true;
};

const openNewTimeOff = (user: PlanningUser, day: VisibleDay) => {
  isEditing.value = false;
  selectedItemId.value = null;
  selectedItemType.value = "time_off";
  planningEditMode.value = "all";
  editingParentId.value = null;

  formData.value = {
    userId: user.id,
    projectId: null,
    startDate: day.date,
    endDate: day.date,
    frequency: null,
    repeatEndDate: null,
    durationPerDay: DEFAULT_DURATION_PER_DAY,
    notes: "",
    tentative: false,
    type: DEFAULT_LEAVE_TYPE,
  };

  showModal.value = true;
};

const { client } = useApolloClient();

const openEditPlannedTime = async (plannedTime: PlanningPlannedTime) => {
  isEditing.value = true;
  selectedItemId.value = plannedTime.id;
  selectedItemType.value = "planned_time";
  planningEditMode.value = "all";
  editingParentId.value = null;

  // Fetch fresh data from backend to get parent planned_time info
  try {
    const { data } = await client.query({
      query: GET_PLANNED_TIME_FOR_EDIT,
      variables: { occurrenceId: plannedTime.id },
      fetchPolicy: "network-only",
    });

    const editData = data?.plannedTimeForEdit;

    // Store both parent and occurrence data for switching between edit modes
    fetchedPlannedTimeParentData.value = editData?.parent ?? null;
    fetchedPlannedTimeOccurrenceData.value = editData?.occurrence ?? null;

    if (editData?.parent) {
      // Store parent ID for "all" mode editing
      editingParentId.value = editData.parent.id;
      // If there's a parent, default to "all" mode, otherwise "single"
      planningEditMode.value = editData.parent.id ? "all" : "single";
      // Use parent data for modal (default to "all" mode)
      formData.value = {
        userId: editData.occurrence.user?.id ?? plannedTime.user.id,
        projectId:
          editData.occurrence.project?.id ?? plannedTime.project?.id ?? null,
        projectRoleId: editData.occurrence.projectRole?.id ?? null,
        startDate: editData.parent.startDate,
        endDate: editData.parent.endDate,
        frequency: editData.parent.frequency,
        repeatEndDate: editData.parent.repeatEndDate,
        durationPerDay:
          editData.parent.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
        notes: editData.parent.notes ?? "",
        tentative: editData.occurrence.tentative ?? false,
        type: DEFAULT_LEAVE_TYPE,
      };
    } else {
      // Fallback to occurrence data if no parent
      fetchedPlannedTimeOccurrenceData.value = plannedTime;
      formData.value = {
        userId: plannedTime.user.id,
        projectId: plannedTime.project?.id ?? null,
        projectRoleId: null,
        startDate: plannedTime.startDate,
        endDate: plannedTime.endDate,
        frequency: plannedTime.frequency ?? null,
        repeatEndDate: plannedTime.repeatEndDate ?? null,
        durationPerDay: plannedTime.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
        notes: plannedTime.notes ?? "",
        tentative: plannedTime.tentative ?? false,
        type: DEFAULT_LEAVE_TYPE,
      };
    }
  } catch (error) {
    console.error("Failed to fetch planned time for edit:", error);
    // Fallback to occurrence data
    formData.value = {
      userId: plannedTime.user.id,
      projectId: plannedTime.project?.id ?? null,
      startDate: plannedTime.startDate,
      endDate: plannedTime.endDate,
      frequency: plannedTime.frequency ?? null,
      repeatEndDate: plannedTime.repeatEndDate ?? null,
      durationPerDay: plannedTime.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
      notes: plannedTime.notes ?? "",
      tentative: plannedTime.tentative ?? false,
      type: DEFAULT_LEAVE_TYPE,
    };
  }

  showModal.value = true;
};

const handleResizePlannedTime = async (
  plannedTime: PlanningPlannedTime,
  newStartDate: string,
  newEndDate: string,
  edge: "left" | "right",
) => {
  try {
    // Calendar items are occurrences. Fetch the parent planned_time first so
    // resizing changes the whole period while preserving the parent's settings.
    const { data } = await client.query({
      query: GET_PLANNED_TIME_FOR_EDIT,
      variables: { occurrenceId: plannedTime.id },
      fetchPolicy: "network-only",
    });

    const editData = data?.plannedTimeForEdit;
    const parent = editData?.parent;
    const occurrence = editData?.occurrence;

    if (!parent?.id) {
      console.error(
        "Cannot resize planned time: parent planned time was not found",
        plannedTime,
      );
      return;
    }

    const projectId =
      occurrence?.project?.id ?? plannedTime.project?.id ?? null;
    const userId = occurrence?.user?.id ?? plannedTime.user.id;

    if (!projectId) {
      console.error(
        "Cannot resize planned time: project was not found",
        plannedTime,
      );
      return;
    }

    let resizedStartDate = edge === "left" ? newStartDate : parent.startDate;
    let resizedEndDate = edge === "right" ? newEndDate : parent.endDate;

    // Minimum period is one day. Clamp against the real parent boundaries,
    // not against the occurrence-local start/end dates.
    if (dayjs(resizedStartDate).isAfter(dayjs(resizedEndDate), "day")) {
      if (edge === "left") {
        resizedStartDate = resizedEndDate;
      } else {
        resizedEndDate = resizedStartDate;
      }
    }

    const result = await updatePlannedTimeMutate({
      input: {
        parentId: parent.id,
        userId,
        projectId,
        projectRoleId: occurrence?.projectRole?.id ?? null,
        startDate: resizedStartDate,
        endDate: resizedEndDate,
        frequency: parent.frequency ?? "no_repeat",
        repeatEndDate: parent.repeatEndDate ?? null,
        durationPerDay:
          parent.durationPerDay ??
          plannedTime.durationPerDay ??
          DEFAULT_DURATION_PER_DAY,
        tentative: occurrence?.tentative ?? plannedTime.tentative ?? false,
        notes: parent.notes ?? plannedTime.notes ?? null,
      },
    });

    const response = result?.data?.updatePlannedTime;

    if (!response || response.status !== "ok") {
      console.error(
        "Failed to resize planned time:",
        response?.error ?? response,
      );
    }
  } catch (error) {
    console.error("Error resizing planned time:", error);
  }
};

const handleMovePlannedTime = async (
  plannedTime: PlanningPlannedTime,
  targetDate: string,
) => {
  try {
    const { data } = await client.query({
      query: GET_PLANNED_TIME_FOR_EDIT,
      variables: { occurrenceId: plannedTime.id },
      fetchPolicy: "network-only",
    });

    const editData = data?.plannedTimeForEdit;
    const parent = editData?.parent;
    const occurrence = editData?.occurrence;

    if (!parent?.id) {
      console.error(
        "Cannot move planned time: parent planned time was not found",
        plannedTime,
      );
      return;
    }

    const projectId =
      occurrence?.project?.id ?? plannedTime.project?.id ?? null;
    const userId = occurrence?.user?.id ?? plannedTime.user.id;

    if (!projectId) {
      console.error(
        "Cannot move planned time: project was not found",
        plannedTime,
      );
      return;
    }

    const dayDelta = dayjs(targetDate)
      .startOf("day")
      .diff(dayjs(plannedTime.startDate).startOf("day"), "day");

    if (dayDelta === 0) {
      return;
    }

    const movedStartDate = dayjs(parent.startDate)
      .add(dayDelta, "day")
      .format("YYYY-MM-DD");
    const movedEndDate = dayjs(parent.endDate)
      .add(dayDelta, "day")
      .format("YYYY-MM-DD");
    const movedRepeatEndDate = parent.repeatEndDate
      ? dayjs(parent.repeatEndDate).add(dayDelta, "day").format("YYYY-MM-DD")
      : null;

    const result = await updatePlannedTimeMutate({
      input: {
        parentId: parent.id,
        userId,
        projectId,
        projectRoleId: occurrence?.projectRole?.id ?? null,
        startDate: movedStartDate,
        endDate: movedEndDate,
        frequency: parent.frequency ?? "no_repeat",
        repeatEndDate: movedRepeatEndDate,
        durationPerDay:
          parent.durationPerDay ??
          plannedTime.durationPerDay ??
          DEFAULT_DURATION_PER_DAY,
        tentative: occurrence?.tentative ?? plannedTime.tentative ?? false,
        notes: parent.notes ?? plannedTime.notes ?? null,
      },
    });

    const response = result?.data?.updatePlannedTime;

    if (!response || response.status !== "ok") {
      console.error(
        "Failed to move planned time:",
        response?.error ?? response,
      );
    }
  } catch (error) {
    console.error("Error moving planned time:", error);
  }
};

const openEditTimeOff = async (timeOff: PlanningTimeOff) => {
  isEditing.value = true;
  selectedItemId.value = timeOff.id;
  selectedItemType.value = "time_off";
  planningEditMode.value = "all";
  editingParentId.value = null;

  // Fetch fresh data from backend to get parent time_off info
  try {
    const { data } = await client.query({
      query: GET_TIME_OFF_FOR_EDIT,
      variables: { occurrenceId: timeOff.id },
      fetchPolicy: "network-only",
    });

    const editData = data?.timeOffForEdit;

    // Store both parent and occurrence data for switching between edit modes
    fetchedTimeOffParentData.value = editData?.parent ?? null;
    fetchedTimeOffOccurrenceData.value = editData?.occurrence ?? null;

    if (editData?.parent) {
      // Store parent ID for "all" mode editing
      editingParentId.value = editData.parent.id;
      // If there's a parent, default to "all" mode, otherwise "single"
      planningEditMode.value = editData.parent.id ? "all" : "single";
      // Use parent data for modal
      formData.value = {
        userId: editData.occurrence.user?.id ?? timeOff.user.id,
        projectId: null,
        projectRoleId: null,
        startDate: editData.parent.startDate,
        endDate: editData.parent.endDate,
        frequency: editData.parent.frequency,
        repeatEndDate: editData.parent.repeatEndDate,
        durationPerDay:
          editData.parent.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
        tentative: false,
        type: (editData.parent.type ?? DEFAULT_LEAVE_TYPE) as LeaveType,
      };
    } else {
      // Fallback to occurrence data if no parent
      fetchedTimeOffOccurrenceData.value = timeOff;
      formData.value = {
        userId: timeOff.user.id,
        projectId: null,
        projectRoleId: null,
        startDate: timeOff.startDate,
        endDate: timeOff.endDate,
        frequency: timeOff.frequency ?? null,
        repeatEndDate: timeOff.repeatEndDate ?? null,
        durationPerDay: timeOff.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
        tentative: false,
        type: (timeOff.type ?? DEFAULT_LEAVE_TYPE) as LeaveType,
      };
    }
  } catch (error) {
    console.error("Failed to fetch time off for edit:", error);
    // Fallback to occurrence data
    formData.value = {
      userId: timeOff.user.id,
      projectId: null,
      startDate: timeOff.startDate,
      endDate: timeOff.endDate,
      frequency: timeOff.frequency ?? null,
      repeatEndDate: timeOff.repeatEndDate ?? null,
      durationPerDay: timeOff.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
      tentative: false,
      type: (timeOff.type ?? DEFAULT_LEAVE_TYPE) as LeaveType,
    };
  }

  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedItemId.value = null;
  selectedItemType.value = null;
  planningEditMode.value = "all";
  editingParentId.value = null;
};

const openNewTimesheet = (user: PlanningUser, day: VisibleDay) => {
  selectedTimesheet.value = {
    date: day.date,
    duration: 0,
    notes: "",
    userId: user.id,
    userName: getUserDisplayName(user),
    projectId: null,
    themeId: null,
    roleId: null,
    activityId: null,
  };

  showTimesheetModal.value = true;
};

const openNewTimesheetFromSuggestion = (suggestion: TimesheetSuggestion) => {
  const projectId = suggestion.plannedTime.project?.id ?? null;
  const fallbackProject = projectId
    ? projects.value.find((project) => project.id === projectId)
    : null;

  selectedTimesheet.value = {
    date: suggestion.day.date,
    duration: Math.round(suggestion.hours * 60),
    notes: "",
    userId: suggestion.user.id,
    userName: getUserDisplayName(suggestion.user),
    projectId,
    themeId: null,
    roleId: suggestion.plannedTime.projectRole?.id ?? null,
    activityId: null,
  };

  console.log("Opening timesheet suggestion", {
    projectId,
    projectName: suggestion.plannedTime.project?.name ?? fallbackProject?.name,
    roleId: suggestion.plannedTime.projectRole?.id ?? null,
    roleName: suggestion.plannedTime.projectRole?.name ?? null,
    suggestion,
  });

  showTimesheetModal.value = true;
};

const openEditTimesheet = (timesheetEntry: PlanningTimesheetEntry) => {
  selectedTimesheet.value = {
    id: timesheetEntry.id,
    date: timesheetEntry.date,
    duration: timesheetEntry.duration,
    notes: timesheetEntry.notes ?? "",
    userId: timesheetEntry.user.id,
    userName: getUserDisplayName(timesheetEntry.user),
    projectId: timesheetEntry.project.id,
    themeId: timesheetEntry.theme.id,
    roleId: timesheetEntry.projectRole.id,
    activityId: null,
  };

  showTimesheetModal.value = true;
};

const closeTimesheetModal = () => {
  showTimesheetModal.value = false;
  selectedTimesheet.value = null;
};

const handleTimesheetUpdated = () => {
  refetchPlanning();
  refetchWeekKpi();
  refetchMonthKpi();
  if (customPeriodKpiEnabled.value) {
    refetchCustomPeriodKpi();
  }
  closeTimesheetModal();
};

const handleTimesheetDeleted = () => {
  refetchPlanning();
  refetchWeekKpi();
  refetchMonthKpi();
  if (customPeriodKpiEnabled.value) {
    refetchCustomPeriodKpi();
  }
  closeTimesheetModal();
};

const savePlanningItem = async () => {
  if (!selectedItemType.value) {
    console.error("Missing item type");
    closeModal();
    return;
  }

  const fd = formData.value;
  const isPlannedTime = selectedItemType.value === "planned_time";

  const userId = fd.userId;
  const startDate = fd.startDate;
  const endDate = fd.endDate;
  const projectId = isPlannedTime ? fd.projectId : null;

  if (!userId || !startDate || !endDate || (isPlannedTime && !projectId)) {
    console.error("Missing required fields for saving planning item", {
      type: selectedItemType.value,
      fd,
    });
    return;
  }

  const input: Record<string, unknown> = {
    userId,
    startDate,
    endDate,
    frequency: fd.frequency || "no_repeat",
    repeatEndDate: fd.repeatEndDate || null,
    durationPerDay: fd.durationPerDay || DEFAULT_DURATION_PER_DAY,
  };

  if (isPlannedTime) {
    input.projectId = projectId;
    input.projectRoleId = fd.projectRoleId ?? null;
    input.notes = fd.notes || null;

    // For create: don't send tentative (will be computed automatically)
    // For edit "all" mode: only send tentative if explicitly set (not null) - means "don't update"
    // For edit "single" mode: always send the tentative value
    if (!selectedItemId.value) {
      // Creating new planned time - tentative will be computed automatically
      // Don't include tentative in input
    } else if (planningEditMode.value === "all" && editingParentId.value) {
      // Editing all occurrences - only include tentative if explicitly set (not null)
      // null means "don't update tentative for children"
      if (fd.tentative !== null && fd.tentative !== undefined) {
        input.tentative = fd.tentative;
      }
    } else {
      // Editing single occurrence - always include tentative
      input.tentative = fd.tentative ?? false;
    }
  }

  if (selectedItemType.value === "time_off") {
    input.type = fd.type ?? DEFAULT_LEAVE_TYPE;
  }

  try {
    let response;

    if (selectedItemType.value === "planned_time") {
      if (selectedItemId.value) {
        if (planningEditMode.value === "all" && editingParentId.value) {
          // Update all occurrences (use parent ID)
          const result = await updatePlannedTimeMutate({
            input: { parentId: editingParentId.value, ...input },
          });
          response = result?.data?.updatePlannedTime;
        } else {
          // Update single occurrence (use occurrence ID) - only send relevant fields
          const result = await updatePlannedTimeOccurrenceMutate({
            input: {
              occurrenceId: selectedItemId.value,
              projectId: input.projectId ?? undefined,
              projectRoleId: input.projectRoleId ?? undefined,
              durationPerDay: input.durationPerDay ?? undefined,
              tentative: input.tentative,
              notes: input.notes ?? undefined,
            },
          });
          response = result?.data?.updatePlannedTimeOccurrence;
        }
      } else {
        const result = await createPlannedTimeMutate({ input });

        response = result?.data?.createPlannedTime;
      }
    }

    if (selectedItemType.value === "time_off") {
      if (selectedItemId.value) {
        if (planningEditMode.value === "all" && editingParentId.value) {
          // Update all occurrences (use parent ID)
          const result = await updateTimeOffMutate({
            input: { parentId: editingParentId.value, ...input },
          });
          response = result?.data?.updateTimeOff;
        } else {
          // Update single occurrence (use occurrence ID) - only send relevant fields
          const result = await updateTimeOffOccurrenceMutate({
            input: {
              occurrenceId: selectedItemId.value,
              durationPerDay: input.durationPerDay ?? undefined,
              type: input.type ?? undefined,
            },
          });
          response = result?.data?.updateTimeOffOccurrence;
        }
      } else {
        const result = await createTimeOffMutate({ input });

        response = result?.data?.createTimeOff;
      }
    }

    if (!response || response.status !== "ok") {
      const errorMessage = response?.error ?? "Failed to save planning item";
      console.error("Failed to save planning item:", errorMessage);
      showPlanningNotification("error", "Error", errorMessage);
      return;
    }

    // Show notification for planned time with adjustment info
    if (selectedItemType.value === "planned_time" && response.data) {
      const data = response.data;
      const hasAdjustments =
        (data.adjustedDates && data.adjustedDates.length > 0) ||
        (data.skippedDates && data.skippedDates.length > 0);

      if (hasAdjustments) {
        const details: string[] = [];
        if (data.adjustedDates?.length > 0) {
          details.push(`${data.adjustedDates.length} date(s) adjusted`);
        }
        if (data.skippedDates?.length > 0) {
          details.push(
            `${data.skippedDates.length} date(s) skipped (no capacity)`,
          );
        }

        showPlanningNotification(
          "warning",
          "Planned time created with adjustments",
          details.join(", "),
        );
      }
    }

    // Show notification for time off with adjustment info
    if (selectedItemType.value === "time_off" && response.data) {
      const data = response.data;
      const hasAdjustments =
        (data.adjustedDates && data.adjustedDates.length > 0) ||
        (data.skippedDates && data.skippedDates.length > 0) ||
        (data.deletedPlannedTimeIds && data.deletedPlannedTimeIds.length > 0) ||
        (data.reducedPlannedTimeIds && data.reducedPlannedTimeIds.length > 0);

      if (hasAdjustments) {
        const details: string[] = [];
        if (data.adjustedDates?.length > 0) {
          details.push(`${data.adjustedDates.length} date(s) adjusted`);
        }
        if (data.skippedDates?.length > 0) {
          details.push(`${data.skippedDates.length} date(s) skipped`);
        }
        if (data.deletedPlannedTimeIds?.length > 0) {
          details.push(
            `${data.deletedPlannedTimeIds.length} planned time(s) deleted`,
          );
        }
        if (data.reducedPlannedTimeIds?.length > 0) {
          details.push(
            `${data.reducedPlannedTimeIds.length} planned time(s) reduced`,
          );
        }

        showPlanningNotification(
          "warning",
          "Time off created with adjustments",
          details.join(", "),
        );
      }
    }

    closeModal();
  } catch (error) {
    console.error("Error saving planning item:", error);
  }
};

const deletePlanningItem = async () => {
  if (!selectedItemId.value || !selectedItemType.value) {
    closeModal();
    return;
  }

  try {
    if (selectedItemType.value === "planned_time") {
      if (planningEditMode.value === "all" && editingParentId.value) {
        // Delete all occurrences (use parent ID)
        await deletePlannedTimeMutate({ id: editingParentId.value });
      } else {
        // Delete single occurrence (use occurrence ID)
        await deletePlannedTimeOccurrenceMutate({ id: selectedItemId.value });
      }
    } else if (selectedItemType.value === "time_off") {
      if (planningEditMode.value === "all" && editingParentId.value) {
        // Delete all occurrences (use parent ID)
        await deleteTimeOffMutate({ id: editingParentId.value });
      } else {
        // Delete single occurrence (use occurrence ID)
        await deleteTimeOffOccurrenceMutate({ id: selectedItemId.value });
      }
    }
  } catch (error) {
    console.error("Error deleting planning item:", error);
  }

  closeModal();
};

// Watch for edit mode changes to update form data
watch(
  () => planningEditMode.value,
  (newMode) => {
    // Only switch data if we have stored data and are editing
    if (!isEditing.value) return;

    if (selectedItemType.value === "planned_time") {
      if (
        fetchedPlannedTimeParentData.value &&
        fetchedPlannedTimeOccurrenceData.value
      ) {
        if (newMode === "single") {
          // Use occurrence data for single mode
          const occ = fetchedPlannedTimeOccurrenceData.value;
          formData.value = {
            userId: occ.user?.id ?? null,
            projectId: occ.project?.id ?? null,
            projectRoleId: occ.projectRole?.id ?? null,
            startDate: occ.startDate,
            endDate: occ.endDate,
            frequency: null,
            repeatEndDate: null,
            durationPerDay: occ.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
            notes: occ.notes ?? "",
            tentative: occ.tentative ?? false,
            type: DEFAULT_LEAVE_TYPE,
          };
        } else {
          // Use parent data for all mode
          const parent = fetchedPlannedTimeParentData.value;
          formData.value = {
            userId: fetchedPlannedTimeOccurrenceData.value.user?.id ?? null,
            projectId:
              fetchedPlannedTimeOccurrenceData.value.project?.id ?? null,
            projectRoleId:
              fetchedPlannedTimeOccurrenceData.value.projectRole?.id ?? null,
            startDate: parent.startDate,
            endDate: parent.endDate,
            frequency: parent.frequency,
            repeatEndDate: parent.repeatEndDate,
            durationPerDay: parent.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
            notes: parent.notes ?? "",
            tentative: parent.tentative ?? false,
            type: DEFAULT_LEAVE_TYPE,
          };
        }
      }
    } else if (selectedItemType.value === "time_off") {
      if (
        fetchedTimeOffParentData.value &&
        fetchedTimeOffOccurrenceData.value
      ) {
        if (newMode === "single") {
          // Use occurrence data for single mode
          const occ = fetchedTimeOffOccurrenceData.value;
          formData.value = {
            userId: occ.user?.id ?? null,
            projectId: null,
            projectRoleId: null,
            startDate: occ.startDate,
            endDate: occ.endDate,
            frequency: null,
            repeatEndDate: null,
            durationPerDay: occ.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
            notes: "",
            tentative: false,
            type: occ.type ?? DEFAULT_LEAVE_TYPE,
          };
        } else {
          // Use parent data for all mode
          const parent = fetchedTimeOffParentData.value;
          formData.value = {
            userId: fetchedTimeOffOccurrenceData.value.user?.id ?? null,
            projectId: null,
            projectRoleId: null,
            startDate: parent.startDate,
            endDate: parent.endDate,
            frequency: parent.frequency,
            repeatEndDate: parent.repeatEndDate,
            durationPerDay: parent.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
            notes: "",
            tentative: false,
            type: parent.type ?? DEFAULT_LEAVE_TYPE,
          };
        }
      }
    }
  },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(PlanningToolbar, {
      "week-range-label": weekRangeLabel.value,
      "has-active-filters": _unref(hasActiveFilters),
      "active-filter-count": _unref(activeFilterCount),
      "view-mode": _unref(viewMode),
      "onUpdate:viewMode": _cache[0] || (_cache[0] = ($event: any) => (viewMode.value = $event)),
      onGoToToday: goToToday,
      onPreviousPeriod: previousPeriod,
      onNextPeriod: nextPeriod,
      onToggleFilters: _unref(toggleFilterPanel)
    }, {
      "left-actions": _withCtx(() => [
        _createVNode(PlanningKpiCard, {
          "week-number": currentWeekNumber.value,
          "week-total": weekTotal.value,
          "week-expected": weekExpected.value,
          "month-label": currentMonthLabel.value,
          "month-total": monthTotal.value,
          "month-expected": monthExpected.value,
          "period-start": customPeriodStart.value,
          "period-end": customPeriodEnd.value,
          "period-total": customPeriodTotal.value,
          "period-expected": customPeriodExpected.value,
          "onUpdate:period": handlePeriodUpdate
        }, null, 8, ["week-number", "week-total", "week-expected", "month-label", "month-total", "month-expected", "period-start", "period-end", "period-total", "period-expected"])
      ]),
      _: 1
    }, 8, ["week-range-label", "has-active-filters", "active-filter-count", "view-mode", "onToggleFilters"]),
    (_unref(showFilterPanel))
      ? (_openBlock(), _createBlock(PlanningFilterPanel, {
          key: 0,
          "active-filter-tab": _unref(activeFilterTab),
          "onUpdate:activeFilterTab": _cache[1] || (_cache[1] = ($event: any) => (_isRef(activeFilterTab) ? (activeFilterTab).value = $event : null)),
          "filter-search": _unref(filterSearch),
          "onUpdate:filterSearch": _cache[2] || (_cache[2] = ($event: any) => (_isRef(filterSearch) ? (filterSearch).value = $event : null)),
          "filter-tabs": _unref(filterTabs),
          "filtered-filter-options": _unref(filteredFilterOptions),
          "has-active-filters": _unref(hasActiveFilters),
          "is-filter-selected": _unref(isFilterSelected),
          onToggleFilter: _unref(toggleFilter),
          onClearAllFilters: _unref(clearAllFilters),
          onClose: _unref(toggleFilterPanel)
        }, null, 8, ["active-filter-tab", "filter-search", "filter-tabs", "filtered-filter-options", "has-active-filters", "is-filter-selected", "onToggleFilter", "onClearAllFilters", "onClose"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("label", _hoisted_3, [
        _withDirectives(_createElementVNode("input", {
          type: "checkbox",
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(showLoggedTimeSuggestions) ? (showLoggedTimeSuggestions).value = $event : null))
        }, null, 512), [
          [_vModelCheckbox, _unref(showLoggedTimeSuggestions)]
        ]),
        _createElementVNode("span", null, _toDisplayString(_unref(t)("planning.loggedTimeSuggestions")), 1)
      ]),
      _createElementVNode("label", _hoisted_4, [
        _withDirectives(_createElementVNode("input", {
          type: "checkbox",
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (_isRef(showLoggedTime) ? (showLoggedTime).value = $event : null))
        }, null, 512), [
          [_vModelCheckbox, _unref(showLoggedTime)]
        ]),
        _createElementVNode("span", null, _toDisplayString(_unref(t)("planning.loggedTime")), 1)
      ]),
      _createElementVNode("label", _hoisted_5, [
        _withDirectives(_createElementVNode("input", {
          type: "checkbox",
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (_isRef(showPlannedTime) ? (showPlannedTime).value = $event : null))
        }, null, 512), [
          [_vModelCheckbox, _unref(showPlannedTime)]
        ]),
        _createElementVNode("span", null, _toDisplayString(_unref(t)("planning.plannedTime")), 1)
      ]),
      _createElementVNode("label", _hoisted_6, [
        _withDirectives(_createElementVNode("input", {
          type: "checkbox",
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => (_isRef(showOnlyMe) ? (showOnlyMe).value = $event : null))
        }, null, 512), [
          [_vModelCheckbox, _unref(showOnlyMe)]
        ]),
        _createElementVNode("span", null, _toDisplayString(_unref(t)("planning.me")), 1)
      ])
    ]),
    _createVNode(PlanningCalendar, {
      users: displayedUsers.value,
      "visible-days": visibleDays.value,
      "planned-times": _unref(filteredPlannedTimes),
      "time-offs": _unref(filteredTimeOffs),
      holidays: filteredHolidays.value,
      projects: projects.value,
      "timesheet-entries": filteredTimesheetEntries.value,
      "show-logged-time": _unref(showLoggedTime),
      "show-planned-time": _unref(showPlannedTime),
      "show-logged-time-suggestions": _unref(showLoggedTimeSuggestions),
      "min-row-height": planningMinRowHeight.value,
      "current-user-id": currentUser.value?.id,
      "is-admin": isUserAdmin.value,
      "month-view": _unref(viewMode) === 'month',
      onOpenNewPlannedTime: openNewPlannedTime,
      onOpenNewTimeOff: openNewTimeOff,
      onOpenEditPlannedTime: openEditPlannedTime,
      onOpenEditTimeOff: openEditTimeOff,
      onOpenNewTimesheet: openNewTimesheet,
      onOpenEditTimesheet: openEditTimesheet,
      onOpenNewTimesheetSuggestion: openNewTimesheetFromSuggestion,
      onResizePlannedTime: handleResizePlannedTime,
      onMovePlannedTime: handleMovePlannedTime
    }, null, 8, ["users", "visible-days", "planned-times", "time-offs", "holidays", "projects", "timesheet-entries", "show-logged-time", "show-planned-time", "show-logged-time-suggestions", "min-row-height", "current-user-id", "is-admin", "month-view"]),
    (showModal.value)
      ? (_openBlock(), _createBlock(PlanningModal, {
          key: 1,
          "form-data": formData.value,
          "onUpdate:formData": _cache[7] || (_cache[7] = ($event: any) => ((formData).value = $event)),
          mode: selectedItemType.value,
          "onUpdate:mode": _cache[8] || (_cache[8] = ($event: any) => ((selectedItemType).value = $event)),
          "edit-mode": planningEditMode.value,
          "onUpdate:editMode": _cache[9] || (_cache[9] = ($event: any) => ((planningEditMode).value = $event)),
          "is-editing": isEditing.value,
          users: displayedUsers.value,
          projects: projects.value,
          "current-project": editingProject.value,
          "has-parent": !!editingParentId.value,
          "occurrence-tentative": occurrenceTentative.value,
          onClose: closeModal,
          onSave: savePlanningItem,
          onDelete: deletePlanningItem
        }, null, 8, ["form-data", "mode", "edit-mode", "is-editing", "users", "projects", "current-project", "has-parent", "occurrence-tentative"]))
      : _createCommentVNode("", true),
    (showTimesheetModal.value && selectedTimesheet.value)
      ? (_openBlock(), _createBlock(EditTimesheet, {
          key: 
        [
          selectedTimesheet.value.id ?? 'new',
          selectedTimesheet.value.userId ?? 'current',
          selectedTimesheet.value.date,
          selectedTimesheet.value.projectId ?? 'no-project',
        ].join('-')
      ,
          timesheet: selectedTimesheet.value,
          onClose: closeTimesheetModal,
          "onUpdate:timesheet": handleTimesheetUpdated,
          onDelete: handleTimesheetDeleted
        }, null, 8, ["timesheet"]))
      : _createCommentVNode("", true)
  ]))
}
}

})